import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X, ArrowRight } from "lucide-react";

/**
 * ExitIntentModal — fires once per browser tab when the user's mouse leaves the
 * viewport from the top (a classic "about to close the tab" signal). Offers the
 * AI Readiness diagnostic as the high-conversion exit-bait.
 *
 * Suppression rules:
 *   - Only on the Home page (where it adds most pipeline; we don't want this
 *     interrupting users mid-service-page).
 *   - Once per browser session (sessionStorage).
 *   - Never on mobile (the trigger isn't reliable there).
 *   - Never if the user has already dismissed the cookie banner with "rejected"
 *     (we respect that signal of low-engagement intent).
 */
const SESSION_KEY = "ekselens.exit.shown";

const ExitIntentModal = ({ enabledOnPath = "/" }) => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.location.pathname !== enabledOnPath) return;
    if (window.innerWidth < 768) return;
    if (window.sessionStorage.getItem(SESSION_KEY) === "1") return;

    const onMouseOut = (e) => {
      // mouse leaving the top edge of the viewport
      if (e.clientY <= 0 && !e.relatedTarget) {
        window.sessionStorage.setItem(SESSION_KEY, "1");
        setOpen(true);
        document.removeEventListener("mouseout", onMouseOut);
      }
    };
    // Wait 8s before arming so we don't trigger on the user's first quick
    // mousemove towards a browser-chrome bookmark
    const armTimer = setTimeout(() => {
      document.addEventListener("mouseout", onMouseOut);
    }, 8000);

    return () => {
      clearTimeout(armTimer);
      document.removeEventListener("mouseout", onMouseOut);
    };
  }, [enabledOnPath]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[10001] bg-black/70 flex items-center justify-center px-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="exit-modal-title"
      onClick={() => setOpen(false)}
      data-testid="exit-intent-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white max-w-lg w-full p-8 lg:p-10 relative"
      >
        <button
          type="button"
          onClick={() => setOpen(false)}
          aria-label="Close"
          className="absolute top-3 right-3 w-8 h-8 grid place-items-center text-zinc-500 hover:text-black"
          data-testid="exit-modal-close"
        >
          <X size={16} />
        </button>
        <div className="ek-eyebrow text-[#7737F6]">Before you go</div>
        <h2
          id="exit-modal-title"
          className="ek-heading text-2xl md:text-3xl mt-3 text-black leading-tight"
        >
          Wondering if your organisation is ready for AI?
        </h2>
        <p className="ek-body text-[15px] mt-4">
          Download our free <strong>AI Readiness 5-Question Self-Assessment</strong> — a 10-minute, board-ready diagnostic with scoring rubric and suggested next actions. No slide pitch.
        </p>
        <div className="flex flex-wrap items-center gap-4 mt-7">
          <Link
            to="/resources"
            onClick={() => setOpen(false)}
            className="ek-btn ek-btn-primary"
            data-testid="exit-modal-cta"
          >
            Get the diagnostic <ArrowRight size={16} />
          </Link>
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="text-zinc-500 hover:text-black text-[13px] underline underline-offset-4"
            data-testid="exit-modal-dismiss"
          >
            No thanks, keep browsing
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExitIntentModal;
